import React from "react";
import { Controller, useForm } from "react-hook-form";
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Delete } from "@mui/icons-material";
import { useApolloClient, useMutation } from "@apollo/client";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
import FormSaveButtons from "../FormSaveButtons/FormSaveButtons";
import {
  RemoveLicense,
  RemoveLicenseVariables,
  SetLicense,
  SetLicenseVariables,
  TherapistProfile,
  SetPsypactNumber,
  SetPsypactNumberVariables,
} from "../../../../../../api/graphql/gql_queries.types";
import {
  GET_THERAPIST_PROFILE,
  THERAPIST_PROFILE_REMOVE_LICENSE,
  THERAPIST_PROFILE_SET_LICENSE,
  THERAPIST_PROFILE_SET_PSYPACT
} from "../../../../../../api/graphql/gql_queries";
import { CancelHandler } from "../ProfileTabs/ProfileTabs.types";
import { usStates, isSecureInput } from "../../../../../../util/util";

const schema = yup.object({
  values: yup.array().of(
    yup.object({
      state: yup.string().required('State is required'),
      value: yup.string()
        .required('License number is required')
        .test('no-html', 'License number cannot contain HTML or script tags', isSecureInput),
      id: yup.string(),
    })
  ),
  isApitHolder: yup.boolean(),
  mobilityNumber: yup
    .string()
    .nullable()
    .transform((value) => value?.trim() || null)
    .when("isApitHolder", {
      is: true,
      then: (s) =>
        s
          .required("Mobility Number is required")
          .matches(/^\d{1,10}$/, "Mobility Number must be 1-10 digits"),
    }),
});

interface ILicenseFormProps {
  onCancel?: CancelHandler;
}

function LicenseForm({ onCancel }: ILicenseFormProps) {
  const client = useApolloClient();

  const therapistData = client.readQuery<TherapistProfile>({
    query: GET_THERAPIST_PROFILE,
  });
  const therapistState = therapistData?.therapistProfile?.state;
  const licenses = therapistState?.therapistLicenses;
  const psypactNumber = therapistState?.therapist?.psypactNumber;

  const { control, handleSubmit, getValues, setValue, watch, formState: { errors } } = useForm<{
    values: { state: string; value: string; id: string }[];
    isApitHolder: boolean;
    mobilityNumber: string;
  }>({
    defaultValues: {
      values: licenses?.map(
        (l) =>
          ({
            value: l!.licenseNumber,
            state: l!.licenseState,
            id: l!.id,
          } as { state: string; value: string; id: string })
      ) ?? [],
      isApitHolder: Boolean(psypactNumber),
      mobilityNumber: psypactNumber || "",
    },
    resolver: yupResolver(schema),
  });

  const isApitHolder = watch("isApitHolder");

  const [titleMutation, { loading, error }] = useMutation<
    SetLicense,
    SetLicenseVariables
  >(THERAPIST_PROFILE_SET_LICENSE, {
    refetchQueries: [{ query: GET_THERAPIST_PROFILE }, "GetProfile"],
  });

  const [removeMutation, { error: removeError }] = useMutation<
    RemoveLicense,
    RemoveLicenseVariables
  >(THERAPIST_PROFILE_REMOVE_LICENSE, {
    refetchQueries: [{ query: GET_THERAPIST_PROFILE }, "GetProfile"],
  });

  const [setPsypactMutation] = useMutation<
    SetPsypactNumber,
    SetPsypactNumberVariables
  >(THERAPIST_PROFILE_SET_PSYPACT, {
    refetchQueries: [{ query: GET_THERAPIST_PROFILE }, "GetProfile"],
  });

  const isNewId = (id: string) => !id || id.startsWith("temp-");

  const addField = () => {
    const state = getValues();
    setValue("values", [...state.values, { state: "", value: "", id: `temp-${Date.now()}`}]);
  };

  return (
    <Box>
      <form
        onSubmit={handleSubmit(async (value) => {
          const validLicenses = value.values.filter(
            (v) => isNewId(v.id) && v.value?.trim() && v.state);

          if (validLicenses.length > 0) {
            await titleMutation({
              variables: {
                entries: validLicenses.map((v) => ({
                  licenseNumber: v.value.trim(),
                  licenseState: v.state,
                })),
              },
            });
          }

          await setPsypactMutation({
            variables: {
              psypactNumber: value.isApitHolder ? value.mobilityNumber : null,
            },
          });

          onCancel?.("Successfully updated your licenses");
        })}
      >
        <Controller
          control={control}
          render={({ field }) => (
            <Stack spacing={2}>
              {field.value.map((f, i) => (
                <FormControl key={f.id} data-test="license-form">
                  <Grid container spacing={1}>
                    <Grid item>
                      <FormControl error={Boolean(errors.values?.[i]?.state)}>
                        <InputLabel id="state-field">State</InputLabel>
                        <Select
                          labelId="state-field"
                          value={f.state}
                          label="State"
                          disabled={!isNewId(f.id)}
                          sx={{ minWidth: "80px" }}
                          data-test="state-selector"
                          onChange={(e) => {
                            const oldValues = [...field.value];
                            oldValues[i].state = e.target.value;
                            field.onChange([...oldValues]);
                          }}
                        >
                          {usStates.map((s) => (
                            <MenuItem key={s.abbreviation} value={s.abbreviation}>
                              {s.abbreviation}
                            </MenuItem>
                          ))}
                        </Select>
                        {errors.values?.[i]?.state && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75, display: 'block' }}>
                            {errors.values?.[i]?.state?.message}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                    <Grid item>
                      <TextField
                        {...field}
                        value={f.value}
                        variant="outlined"
                        label="License Number"
                        disabled={!isNewId(f.id)}
                        sx={{ maxWidth: "280px" }}
                        error={Boolean(errors.values?.[i]?.value)}
                        helperText={errors.values?.[i]?.value?.message}
                        onChange={(e) => {
                          const oldValues = [...field.value];
                          oldValues[i].value = e.target.value;
                          field.onChange([...oldValues]);
                        }}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                aria-label="remove license"
                                onClick={async () => {
                                  if (!isNewId(f.id)) {
                                    await removeMutation({
                                      variables: {
                                        id: f.id!,
                                      },
                                    });
                                  }
                                  field.onChange(
                                    field.value.filter(
                                      (d) => d !== field.value[i]
                                    )
                                  );
                                }}
                                edge="end"
                                color="secondary"
                              >
                                <Delete />
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                </FormControl>
              ))}
            </Stack>
          )}
          name="values"
        />
        <Button variant="text" onClick={addField} sx={{ mt: 2 }}>
          Add License
        </Button>

        <Box sx={{ mt: 2, mb: 2 }}>
          <Controller
            control={control}
            name="isApitHolder"
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value}
                    onChange={field.onChange}
                  />
                }
                label="PSYPACT: I am an APIT holder"
              />
            )}
          />
          <Controller
            control={control}
            name="mobilityNumber"
            render={({ field }) => (
              <>
                <TextField
                  {...field}
                  label="Mobility Number"
                  variant="outlined"
                  disabled={!isApitHolder}
                  error={Boolean(errors.mobilityNumber)}
                  helperText={errors.mobilityNumber?.message}
                  inputProps={{ maxLength: 10 }}
                  sx={{ mt: 1, maxWidth: "280px", display: "block" }}
                />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, mb: 3, display: "block" }}
                >
                  Note: Your mobility number will only enable cross-state matching if you have a license in a PSYPACT participating state.
                </Typography>
              </>
            )}
          />
        </Box>

        <FormSaveButtons
          onCancel={onCancel}
          loading={loading}
          error={Boolean(error || removeError)}
        />
      </form>
    </Box>
  );
}

export default LicenseForm;